import db, {
  HustroDbEntityTablesUnion,
  HustroDbTablesUnion,
} from './index';
import { logWithRethrow } from './helpers';
import { PromiseExtended, Table } from 'dexie';
import { wrapQuery } from './queryWrapper';
import { clear as dbInspectionsClear } from './inspections/inspections';
import { clear as dbInspectionTemplatesClear } from './inspectionTemplates/inspectionTemplates';
import { clear as dbCompaniesClear } from './companies/companies';
import { clear as dbContractsClear } from './contracts/contracts';
import { clear as dbProjectsClear } from './projects/projects';
import { clear as dbOrganizationsClear } from './organizations/organizations';
import { clear as dbUsersClear } from './users/users';
import { clear as dbIssuesClear } from './issues/issues';
import { clear as dbSitesClear } from './sites/sites';
import { clear as dbWorktypesClear } from './fieldValues/worktypes/worktypes';
import { clear as dbHazardCategoriesClear } from './fieldValues/hazardCategories/hazardCategories';
import { clear as dbEnvironmentalAspectsClear } from './fieldValues/environmentalAspects/environmentalAspects';
import { clear as dbCorrectiveActionTypesClear } from './fieldValues/correctiveActionTypes/correctiveActionTypes';
import { clear as dbRejectionReasonsClear } from './fieldValues/rejectionReasons/rejectionReasons';
import { clear as dbDelayReasonsClear } from './fieldValues/delayReasons/delayReasons';
import { clear as dbIssueVerificationItemsClear } from './fieldValues/issueVerificationItems/issueVerificationItems';
import { clear as dbRootCausesClear } from './fieldValues/rootCauses/rootCauses';
import { clear as dbEffectsClear } from './fieldValues/effects/effects';
import { clear as dbCircumstancesClear } from './fieldValues/circumstances/circumstances';
import { clear as dbImpactsClear } from './fieldValues/impacts/impacts';
import { clear as dbDecisionsToImposeFineClear } from './fieldValues/decisionsToImposeFine/decisionsToImposeFine';
import { clear as dbSpilledSubstancesClear } from './fieldValues/spilledSubstances/spilledSubstances';
import { clear as dbPrioritiesClear } from './fieldValues/priorities/priorities';
import { clear as dbProjectStagesClear } from './fieldValues/projectStages/projectStages';
import { clear as dbEmploymentTypesClear } from './fieldValues/employmentTypes/employmentTypes';
import { clear as dbInjuryTypesClear } from './fieldValues/injuryTypes/injuryTypes';
import { clear as dbInjuredBodyPartsClear } from './fieldValues/injuredBodyParts/injuredBodyParts';
import { clear as dbRequirementsClear } from './fieldValues/requirements/requirements';
import { clear as dbInstallationBasisClear } from './fieldValues/installationBasis/installationBasis';
import { clear as dbConstructionProductDocumentsClear } from './fieldValues/constructionProductDocuments/constructionProductDocuments';
import { clear as dbDeviceDocumentsClear } from './fieldValues/deviceDocuments/deviceDocuments';
import { clear as dbAdditionalDocumentsClear } from './fieldValues/additionalDocuments/additionalDocuments';
import { clear as dbLevelsClear } from './levels/levels';
import { clear as dbDocumentationsClear } from './documentations/documentations';
import { clear as dbDirectoriesClear } from './directories/directories';
import { clear as dbIssueFormClear } from './issueForm/issueForm';
import { clear as dbIssueWorkflowsClear } from './issueWorkflows/issueWorkflows';
import { clear as dbVisibleFieldsClear } from './visibleFields/visibleFields';
import { removeSettled as removeSettledDocuments } from './documents';
import { LogLevel } from 'shared/types/logger';
import { debugLog } from 'shared/logger/debugLog';
import { isDownloadedForAnotherProject } from './service';
import { logCollectionsRepairedAtStart } from 'serviceWorker/helpers/dataLog';
import { EntitySyncProblem, findEntitySyncProblem } from 'shared/domain/serviceData/entitySync';
import { type CollectionRepair, CollectionRepairReason } from './collectionRepairReason';
import { readServiceTablesSnapshot } from './serviceTablesSnapshot';

const SKIP_ON_CLEAR = ['config', 'permissions'];
/**
 * Kept on a project switch. Projects and organizations belong to the user, not
 * the project: clearing them would empty the project list.
 */
const SKIP_ON_PROJECT_CLEAR = [
  ...SKIP_ON_CLEAR,
  'selectedProject',
  'chartFilters',
  'projects',
  'projectsService',
  'organizations',
  'organizationsService',
  'httpRequests',
];

/**
 * Built on use, not at load: the clear functions come from modules that import
 * this one back, so they may not be initialized yet when it loads.
 */
function getEntityTableClearers(): Record<HustroDbEntityTablesUnion, CallableFunction> {
  return {
    issues: dbIssuesClear,
    inspections: dbInspectionsClear,
    inspectionTemplates: dbInspectionTemplatesClear,
    companies: dbCompaniesClear,
    contracts: dbContractsClear,
    projects: dbProjectsClear,
    organizations: dbOrganizationsClear,
    users: dbUsersClear,
    sites: dbSitesClear,
    worktypes: dbWorktypesClear,
    hazardCategories: dbHazardCategoriesClear,
    environmentalAspects: dbEnvironmentalAspectsClear,
    correctiveActionTypes: dbCorrectiveActionTypesClear,
    rejectionReasons: dbRejectionReasonsClear,
    delayReasons: dbDelayReasonsClear,
    issueVerificationItems: dbIssueVerificationItemsClear,
    rootCauses: dbRootCausesClear,
    effects: dbEffectsClear,
    circumstances: dbCircumstancesClear,
    impacts: dbImpactsClear,
    decisionsToImposeFine: dbDecisionsToImposeFineClear,
    spilledSubstances: dbSpilledSubstancesClear,
    priorities: dbPrioritiesClear,
    projectStages: dbProjectStagesClear,
    employmentTypes: dbEmploymentTypesClear,
    injuryTypes: dbInjuryTypesClear,
    injuredBodyParts: dbInjuredBodyPartsClear,
    requirements: dbRequirementsClear,
    installationBasis: dbInstallationBasisClear,
    constructionProductDocuments: dbConstructionProductDocumentsClear,
    deviceDocuments: dbDeviceDocumentsClear,
    additionalDocuments: dbAdditionalDocumentsClear,
    levels: dbLevelsClear,
    documentations: dbDocumentationsClear,
    directories: dbDirectoriesClear,
    issueForm: dbIssueFormClear,
    issueWorkflows: dbIssueWorkflowsClear,
    visibleFields: dbVisibleFieldsClear,
  };
}

function isHustroDbEntityTable(
  name: string
): name is HustroDbEntityTablesUnion {
  return name in getEntityTableClearers();
}

const clearDb = wrapQuery(
  db,
  (tableToClear: Table): PromiseExtended<void> => {
    if (isHustroDbEntityTable(tableToClear.name)) {
      return getEntityTableClearers()[tableToClear.name]();
    }
    return tableToClear.clear().catch((e) => {
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: 'Could not clear database table',
        errorObj: e,
        additionalInfo: null,
      });
    });
  }
);

function logFlushDuration(scope: string, startedAt: number): void {
  debugLog(`${scope} completed`, {
    durationMs: Date.now() - startedAt,
  });
}

const flush = wrapQuery(db, (): Promise<any> => {
  const startedAt = Date.now();
  const promises: Promise<any>[] = [];
  db.tables.forEach((table) => {
    debugLog('flushing table:', table);

    const name = table.core.name;
    // @ts-ignore don't know the exact type of db
    db[name] && promises.push(clearDb(db[name]));
  });
  return Promise.all(promises).finally(() => {
    logFlushDuration('flush', startedAt);
  });
});

/**
 * Tables a project switch clears only in part: what still waits for the
 * server survives it (PT-7171: a photo taken offline was lost on a switch).
 */
const PARTIAL_CLEARS_ON_PROJECT_CLEAR: Partial<Record<HustroDbTablesUnion, () => Promise<unknown>>> = {
  documents: () => removeSettledDocuments(),
};

/** Clears a table of the project's data: in part when something in it waits for the server. */
function clearProjectTable(name: HustroDbTablesUnion): Promise<unknown> {
  const partialClear = PARTIAL_CLEARS_ON_PROJECT_CLEAR[name];
  return partialClear ? partialClear() : clearDb(db[name]);
}

export const flushProjectData = wrapQuery(db, (): Promise<any> => {
  const startedAt = Date.now();
  const clearPromises: Promise<any>[] = [];
  db.tables.forEach((table) => {
    const name = table.core.name as HustroDbTablesUnion;
    if (
      !db[name] ||
      SKIP_ON_PROJECT_CLEAR.includes(name) ||
      // services are cleared when entity repository is cleared
      name.endsWith('Service')
    ) {
      return;
    }
    clearPromises.push(clearProjectTable(name));
  });
  return Promise.all(clearPromises).finally(() => {
    logFlushDuration('flushProjectData', startedAt);
  });
});

export const flushTableWithService = wrapQuery(
  db,
  (tableName: HustroDbEntityTablesUnion): Promise<any> => {
    return clearDb(db[tableName]);
  }
);

/** The collections of project data downloaded for another project than the configured one; data of the user is skipped. */
export const findCollectionsOfAnotherProject = wrapQuery(
  db,
  async (): Promise<{ projectId: string | undefined; tables: HustroDbEntityTablesUnion[] }> => {
    const projectId = (await db.config.toCollection().last())?.projectId;
    if (!projectId) {
      return { projectId, tables: [] };
    }
    const tableNames = (Object.keys(getEntityTableClearers()) as HustroDbEntityTablesUnion[]).filter(
      (name) => !SKIP_ON_PROJECT_CLEAR.includes(name)
    );
    const tables = (
      await Promise.all(
        tableNames.map(async (name) => {
          const serviceTable = db[`${name}Service` as HustroDbTablesUnion];
          const state = await serviceTable?.toCollection().last();
          return isDownloadedForAnotherProject(state, projectId) ? name : undefined;
        })
      )
    ).filter((name): name is HustroDbEntityTablesUnion => name !== undefined);
    return { projectId, tables };
  }
);

/** A stored state no sync gets out of: the collection is cleared and downloaded again. */
const REPAIR_OF_SYNC_PROBLEM: Record<EntitySyncProblem, CollectionRepairReason> = {
  // At start no download runs: the flag was left by a stopped worker and blocks every sync.
  [EntitySyncProblem.downloading]: CollectionRepairReason.downloadingAtStart,
  [EntitySyncProblem.emptyAfterDownload]: CollectionRepairReason.emptyAfterDownload,
};

/** The collections that cannot be used as stored, each with the first reason found. */
export const findCollectionsToRepair = wrapQuery(db, async (): Promise<CollectionRepair[]> => {
  const [snapshot, { tables: tablesOfAnotherProject }] = await Promise.all([
    readServiceTablesSnapshot(),
    findCollectionsOfAnotherProject(),
  ]);
  const repairs = new Map<string, CollectionRepairReason>(
    tablesOfAnotherProject.map((table) => [table, CollectionRepairReason.ofAnotherProject])
  );
  Object.entries(snapshot).forEach(([table, entitySnapshot]) => {
    const problem = findEntitySyncProblem(entitySnapshot);
    if (problem && isHustroDbEntityTable(table) && !repairs.has(table)) {
      repairs.set(table, REPAIR_OF_SYNC_PROBLEM[problem]);
    }
  });
  return [...repairs].map(([table, reason]) => ({ table, reason }));
});

/**
 * Clears, before the services start, the collections no sync gets out of: a
 * download a stopped worker left running, a downloaded table that lost its
 * rows, a collection of another project (an unfinished switch). They are
 * downloaded again when read. Data stored before owners were recorded stays.
 */
export const repairCollectionsAtStart = wrapQuery(db, async (): Promise<void> => {
  const repairs = await findCollectionsToRepair();
  if (!repairs.length) {
    return;
  }
  await Promise.all(repairs.map(({ table }) => flushTableWithService(table as HustroDbEntityTablesUnion)));
  await logCollectionsRepairedAtStart(repairs);
});

export const flushData = wrapQuery(db, (): Promise<any> => {
  const clearPromises: Promise<any>[] = [];
  db.tables.forEach((table) => {
    const name = table.core.name as HustroDbTablesUnion;
    if (!SKIP_ON_CLEAR.includes(name) && db[name]) {
      clearPromises.push(clearDb(db[name]));
    }
  });
  return Promise.all(clearPromises);
});

export default flush;
